import React, { useState, useCallback, useRef } from 'react';
import { 
  Wand2, Image as ImageIcon, SunMedium, Sparkles, X, Check, Loader2, 
  Upload, RefreshCw, Download, Palette, Contrast, ZoomIn, Users, User, Layers
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axiosInstance from '../api/axios';

// Background removal models
const BG_MODELS = [
  { 
    id: 'isnet-general-use', 
    name: 'Standard', 
    description: 'Einzelne Objekte',
    icon: User
  },
  { 
    id: 'birefnet-general', 
    name: 'Multi-Objekt', 
    description: 'Mehrere Objekte',
    icon: Users
  },
  { 
    id: 'u2net', 
    name: 'Klassisch', 
    description: 'Einfache Bilder',
    icon: Layers
  },
];

// Filter-Definitionen mit Vorschau-Styles
const FILTERS = [
  { 
    id: 'original', 
    name: 'Original', 
    description: 'Keine Änderung',
    icon: ImageIcon,
    previewStyle: {}
  },
  { 
    id: 'bright_clean', 
    name: 'Hell & Klar', 
    description: 'Heller, frischer Look',
    icon: SunMedium,
    previewStyle: { filter: 'brightness(1.15) contrast(1.05)' }
  },
  { 
    id: 'warm_glow', 
    name: 'Warmes Licht', 
    description: 'Warme, einladende Töne',
    icon: Sparkles,
    previewStyle: { filter: 'brightness(1.08) sepia(0.1) saturate(1.1)' }
  },
  { 
    id: 'high_contrast', 
    name: 'Hoher Kontrast', 
    description: 'Knackige Farben',
    icon: Contrast,
    previewStyle: { filter: 'contrast(1.2) saturate(1.15) brightness(1.05)' }
  },
  { 
    id: 'soft_light', 
    name: 'Sanftes Licht', 
    description: 'Weiches, professionelles Licht',
    icon: SunMedium,
    previewStyle: { filter: 'brightness(1.1) contrast(0.95) saturate(0.95)' }
  },
  { 
    id: 'vivid', 
    name: 'Lebendig', 
    description: 'Satte, intensive Farben',
    icon: Palette,
    previewStyle: { filter: 'saturate(1.25) contrast(1.1) brightness(1.02)' }
  },
  { 
    id: 'studio', 
    name: 'Studio', 
    description: 'Professioneller Produktfoto-Look',
    icon: ZoomIn,
    previewStyle: { filter: 'brightness(1.12) contrast(1.08) saturate(1.05)' }
  },
  { 
    id: 'natural', 
    name: 'Natürlich', 
    description: 'Natürliche, echte Farben',
    icon: Wand2,
    previewStyle: { filter: 'brightness(1.05)' }
  },
];

const ProductImageEditor = ({ 
  isOpen, 
  onClose, 
  imageFile, 
  imageUrl, 
  onSave 
}) => {
  const [selectedFilter, setSelectedFilter] = useState('original');
  const [removeBackground, setRemoveBackground] = useState(false);
  const [backgroundType, setBackgroundType] = useState('white');
  const [bgModel, setBgModel] = useState('isnet-general-use');
  const [processing, setProcessing] = useState(false);
  const [processedImage, setProcessedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(imageUrl);
  
  // Store the ORIGINAL image URL that never changes during the editing session
  const [originalImageUrl, setOriginalImageUrl] = useState(imageUrl);
  
  // Reset state when dialog opens - store the original URL
  React.useEffect(() => {
    if (isOpen) {
      setSelectedFilter('original');
      setRemoveBackground(false);
      setBgModel('isnet-general-use');
      setProcessedImage(null);
      setPreviewImage(imageUrl);
      setOriginalImageUrl(imageUrl); // Save the original URL when dialog opens
    }
  }, [isOpen, imageUrl]);

  const applyChanges = async () => {
    if (!imageFile && !originalImageUrl) {
      toast.error('Kein Bild zum Bearbeiten vorhanden');
      return;
    }
    
    setProcessing(true);
    
    try {
      const formData = new FormData();
      
      // ALWAYS use the original image for processing, not the processed one
      if (imageFile) {
        formData.append('file', imageFile);
      } else if (originalImageUrl) {
        // Try to fetch the ORIGINAL image, but handle CORS errors
        try {
          const response = await fetch(originalImageUrl, { mode: 'cors' });
          if (response.ok) {
            const blob = await response.blob();
            formData.append('file', blob, 'image.jpg');
          } else {
            // If fetch fails, send the URL for backend to handle
            formData.append('image_url', originalImageUrl);
          }
        } catch (corsError) {
          console.log('CORS error, sending URL to backend:', corsError);
          formData.append('image_url', originalImageUrl);
        }
      }
      
      formData.append('filter_name', selectedFilter);
      formData.append('remove_bg', removeBackground.toString());
      formData.append('background', backgroundType);
      formData.append('bg_model', bgModel);
      
      const result = await axiosInstance.post('/image-editor/process-product-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000  // 60 second timeout for image processing
      });
      
      if (result.data.success) {
        setProcessedImage(result.data.image);
        setPreviewImage(result.data.image);
        toast.success('Bild erfolgreich bearbeitet!');
      } else {
        toast.error(result.data.error || 'Fehler bei der Bildbearbeitung');
      }
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Fehler bei der Bildbearbeitung: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleSave = async () => {
    // If changes were selected but not yet applied, apply them first
    if ((selectedFilter !== 'original' || removeBackground) && !processedImage) {
      setProcessing(true);
      try {
        const formData = new FormData();
        
        // Use original image for processing
        if (imageFile) {
          formData.append('file', imageFile);
        } else if (originalImageUrl) {
          // Try to fetch the ORIGINAL image, but handle CORS errors
          try {
            const response = await fetch(originalImageUrl, { mode: 'cors' });
            if (response.ok) {
              const blob = await response.blob();
              formData.append('file', blob, 'image.jpg');
            } else {
              formData.append('image_url', originalImageUrl);
            }
          } catch (corsError) {
            console.log('CORS error, sending URL to backend:', corsError);
            formData.append('image_url', originalImageUrl);
          }
        }
        
        formData.append('filter_name', selectedFilter);
        formData.append('remove_bg', removeBackground.toString());
        formData.append('background', backgroundType);
        formData.append('bg_model', bgModel);
        
        const result = await axiosInstance.post('/image-editor/process-product-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });
        
        if (result.data.success) {
          onSave(result.data.image);
          toast.success('Bild erfolgreich bearbeitet und gespeichert!');
        } else {
          toast.error(result.data.error || 'Fehler bei der Bildbearbeitung');
        }
      } catch (error) {
        console.error('Error processing image:', error);
        toast.error('Fehler bei der Bildbearbeitung: ' + (error.response?.data?.detail || error.message));
      } finally {
        setProcessing(false);
      }
    } else if (processedImage) {
      onSave(processedImage);
      toast.success('Bild gespeichert!');
    } else {
      onSave(originalImageUrl); // Return original if no changes
    }
    onClose();
  };

  const resetChanges = () => {
    // Reset to the ORIGINAL image that was loaded when the dialog opened
    setSelectedFilter('original');
    setRemoveBackground(false);
    setProcessedImage(null);
    setPreviewImage(originalImageUrl); // Always reset to original, not the potentially modified imageUrl
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-[#AF9774]" />
            Produktbild bearbeiten
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Preview Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-gray-700">Vorschau</h3>
              <div 
                className="aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center border-2"
                style={{ borderColor: '#E8DED3' }}
              >
                {previewImage ? (
                  <img 
                    src={previewImage} 
                    alt="Preview"
                    className="max-w-full max-h-full object-contain"
                    style={processedImage ? {} : FILTERS.find(f => f.id === selectedFilter)?.previewStyle}
                  />
                ) : (
                  <div className="text-gray-400 text-center">
                    <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                    <p>Kein Bild</p>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  onClick={resetChanges}
                  variant="outline"
                  className="flex-1"
                  disabled={processing}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Zurücksetzen
                </Button>
                <Button 
                  onClick={applyChanges}
                  className="flex-1"
                  style={{ backgroundColor: '#AF9774' }}
                  disabled={processing}
                >
                  {processing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-2" />
                  )}
                  {processing ? 'Verarbeite...' : 'Anwenden'}
                </Button>
              </div>
            </div>
            
            {/* Controls Section */}
            <div className="space-y-6">
              {/* Filters */}
              <div>
                <h3 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Filter auswählen
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {FILTERS.map(filter => {
                    const Icon = filter.icon;
                    const isSelected = selectedFilter === filter.id;
                    return (
                      <button
                        key={filter.id}
                        onClick={() => setSelectedFilter(filter.id)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          isSelected 
                            ? 'border-[#AF9774] bg-[#F5EDE4]' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${isSelected ? 'text-[#AF9774]' : 'text-gray-400'}`} />
                          <span className={`font-medium text-sm ${isSelected ? 'text-[#AF9774]' : 'text-gray-700'}`}>
                            {filter.name}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{filter.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Background Removal */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5EDE4' }}>
                <h3 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
                  <Wand2 className="h-4 w-4" />
                  Hintergrund entfernen (KI)
                </h3>
                
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={removeBackground}
                    onChange={(e) => setRemoveBackground(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-[#AF9774] focus:ring-[#AF9774]"
                  />
                  <span className="text-sm">Hintergrund automatisch entfernen</span>
                </label>
                
                {removeBackground && (
                  <div className="ml-8 space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Hintergrund ersetzen mit:</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setBackgroundType('white')}
                          className={`px-4 py-2 rounded-none text-sm transition-all ${
                            backgroundType === 'white'
                              ? 'bg-white border-2 border-[#AF9774] text-[#AF9774]'
                              : 'bg-white border border-gray-300 text-gray-600'
                          }`}
                        >
                          ⬜ Weiß
                        </button>
                        <button
                          onClick={() => setBackgroundType('transparent')}
                          className={`px-4 py-2 rounded-none text-sm transition-all ${
                            backgroundType === 'transparent'
                              ? 'bg-white border-2 border-[#AF9774] text-[#AF9774]'
                              : 'bg-white border border-gray-300 text-gray-600'
                          }`}
                          style={{
                            background: backgroundType === 'transparent' 
                              ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                              : undefined,
                            backgroundSize: '10px 10px',
                            backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px'
                          }}
                        >
                          🔲 Transparent
                        </button>
                      </div>
                    </div>
                    
                    {/* Model Selection */}
                    <div>
                      <p className="text-xs text-gray-500 mb-2">KI-Modell:</p>
                      <div className="grid grid-cols-3 gap-2">
                        {BG_MODELS.map((model) => {
                          const ModelIcon = model.icon;
                          return (
                            <button
                              key={model.id}
                              onClick={() => setBgModel(model.id)}
                              className={`p-2 rounded-none text-xs transition-all flex flex-col items-center gap-1 ${
                                bgModel === model.id
                                  ? 'bg-[#AF9774] text-white border-2 border-[#8B7355]'
                                  : 'bg-white border border-gray-300 text-gray-600 hover:border-gray-400'
                              }`}
                            >
                              <ModelIcon className="h-4 w-4" />
                              <span className="font-medium">{model.name}</span>
                              <span className={`text-[10px] ${bgModel === model.id ? 'text-white/80' : 'text-gray-400'}`}>
                                {model.description}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-3">
                  💡 Die KI entfernt den Hintergrund automatisch und erhält saubere Kanten um das Produkt.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex-shrink-0 flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSave}
            style={{ backgroundColor: '#AF9774' }}
            disabled={processing}
          >
            <Check className="h-4 w-4 mr-2" />
            Übernehmen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductImageEditor;
